import { BehaviorSubject, Observable, Observer, Subject, tap, throwError } from "rxjs";
import { InternationalizationService, LocalizationService } from "@archipad-js/core/translate";
import { BaseEntity, toBaseEntityInternal } from "./base-entity";
import {
    DynamicEntityConstructor,
    EntityTypes,
    ExportKind,
    FlushDataHandler,
    RelationDescription,
    Validator,
    Notification,
} from "./types";
import { IllegalArgumentError, IllegalStateError } from "@archipad-js/core/error";
import { ORMError } from "./errors";
import { DynamicEntities } from "./dynamic-entities";
import { generateId } from "./id-generator";

export class EntityContext {
    private _validatorsEnabled = false;

    private _isDirty: boolean;
    private _flushDataHandler!: FlushDataHandler;

    // all orm modification since last commit()
    private _createdEntities!: { [entityName: string]: { [entityId: string]: BaseEntity } };
    private _modifiedEntities!: { [entityName: string]: { [entityId: string]: any } };
    private _deletedEntities!: { [entityName: string]: { [entityId: string]: boolean } };

    private _createdAttachments!: { [entityName: string]: { [entityId: string]: { [key: string]: any } } };
    private _updatedAttachments!: { [entityName: string]: { [entityId: string]: { [key: string]: any } } };

    private _notifications: Subject<Notification>;
    private _notificationsObservable: Observable<Notification>;
    private _subscribers: Set<Observer<unknown>> = new Set();
    private _subscriberCount$: BehaviorSubject<number>;

    private _entitiesByType: { [name: string]: { [entityId: string]: BaseEntity } }; // all orm entities
    private _deletedEntitiesByType: { [name: string]: { [entityId: string]: boolean } }; // entities that once existed but were deleted

    private _modelVersion: number;

    private _modelMinVersionSub$: BehaviorSubject<number>;

    get modelVersion(): number {
        return this._modelVersion;
    }

    get modelMinVersion(): number {
        return this._modelMinVersionSub$.getValue();
    }

    get modelMinVersion$(): Observable<number> {
        return this._modelMinVersionSub$.asObservable();
    }

    patchHandlers: any[];

    constructor(
        public readonly localizationService: LocalizationService,
        public readonly internalizationService: InternationalizationService,
        public entityTypes: EntityTypes,
        private dynamicEntities: DynamicEntities,
        private validators: readonly Validator[],
        metadata: any,
    ) {
        this._subscribers.clear();
        const subscriberCount$ = new BehaviorSubject<number>(0);
        const notifications = new Subject<Notification>();
        const notificationsObservable = new Observable<Notification>((subscriber) => {
            // on unsubscribe
            this._subscribers.add(subscriber);
            subscriberCount$.next(this._subscribers.size);
            return notifications
                .pipe(
                    tap({
                        // on unsubscribe
                        finalize: () => {
                            this._subscribers.delete(subscriber);
                            subscriberCount$.next(this._subscribers.size);
                        },
                    }),
                )
                .subscribe(subscriber);
        });

        this._isDirty = false;

        this._notifications = notifications;
        this._notificationsObservable = notificationsObservable;
        this._subscriberCount$ = subscriberCount$;

        this._modelVersion = parseInt(metadata.versionIdentifiers[0], 10);
        this._modelMinVersionSub$ = new BehaviorSubject(metadata.minVersion);

        this.patchHandlers = metadata.patchHandlers || [];

        this._entitiesByType = {};
        for (const entityName in this.entityTypes.byName) {
            this._entitiesByType[entityName] = {};
        }
        this._deletedEntitiesByType = {};
        for (const entityName in this.entityTypes.byName) {
            this._deletedEntitiesByType[entityName] = {};
        }

        this.commit();
    }

    dispose() {
        for (const entityType in this._entitiesByType) {
            const entities = this._entitiesByType[entityType];
            for (const entityId in entities) {
                const entity = entities[entityId];
                toBaseEntityInternal(entity).entityContext = null;
            }
        }

        this.validators = null;

        this._flushDataHandler = null;

        this._createdEntities = null;
        this._modifiedEntities = null;
        this._deletedEntities = null;

        this._createdAttachments = null;
        this._updatedAttachments = null;

        this._notifications = null;

        this.entityTypes = null;
        this._entitiesByType = null;
        this._deletedEntitiesByType = null;
    }

    private _addRelation(target: BaseEntity, name: string, property: RelationDescription, val: BaseEntity) {
        if (property.single) {
            target[name] = val;
        } else {
            target[name].push(val);
        }
    }
    private _removeRelation(target: BaseEntity, name: string, property: RelationDescription, val: BaseEntity) {
        if (property.single) {
            target[name] = null;
        } else {
            const rel = target[name];
            if (!rel) {
                return;
            }
            const idx = rel.indexOf(val);
            if (idx >= 0) {
                rel.splice(idx, 1);
            }
        }
    }
    private _updateRelationInverse(
        target: BaseEntity,
        property: RelationDescription,
        val: BaseEntity,
        oldVal: BaseEntity,
    ) {
        if (!property.inverseField) {
            return;
        }

        // maintain the other side of the relation
        const inverseProperty = property.target.metadata[property.inverseField] as RelationDescription;
        if (oldVal && !toBaseEntityInternal(oldVal).state.updating) {
            this._removeRelation(oldVal, property.inverseField, inverseProperty, target);
        }
        if (val && !toBaseEntityInternal(val).state.updating) {
            this._addRelation(val, property.inverseField, inverseProperty, target);
        }
    }

    get createdEntities(): { readonly [entityName: string]: { readonly [entityId: string]: BaseEntity } } {
        return this._createdEntities;
    }
    get modifiedEntities(): { readonly [entityName: string]: { readonly [entityId: string]: any } } {
        return this._modifiedEntities;
    }
    get deletedEntities(): { readonly [entityName: string]: { readonly [entityId: string]: boolean } } {
        return this._deletedEntities;
    }

    get createdAttachments(): {
        readonly [entityName: string]: { readonly [entityId: string]: { readonly [key: string]: any } };
    } {
        return this._createdAttachments;
    }
    get updatedAttachments(): {
        readonly [entityName: string]: { readonly [entityId: string]: { readonly [key: string]: any } };
    } {
        return this._updatedAttachments;
    }

    get notifications(): Observable<Notification> {
        return this._notificationsObservable;
    }
    get subscribers(): ReadonlySet<Observer<unknown>> {
        return this._subscribers;
    }
    get subscriberCount(): Observable<number> {
        return this._subscriberCount$;
    }

    get entitiesByType(): { readonly [name: string]: { readonly [entityId: string]: BaseEntity } } {
        return this._entitiesByType;
    }

    commit() {
        this._createdEntities = {};
        this._modifiedEntities = {};
        this._deletedEntities = {};

        this._createdAttachments = {};
        this._updatedAttachments = {};

        this.setDirty(false);

        for (const entityName in this.entityTypes.byName) {
            this._createdEntities[entityName] = {};
            this._modifiedEntities[entityName] = {};
            this._deletedEntities[entityName] = {};
        }

        this._notifyListeners("didCommit");
    }
    setDirty(dirty: boolean) {
        this._isDirty = dirty;
    }
    isDirty() {
        return this._isDirty;
    }

    /**
     * Ensure the project modelMinVersion is superior or equal to the given one.
     *
     * @param minVersion  The minVersion to ensure.
     */
    ensureModelMinVersion(minVersion: number): void {
        if (minVersion === null) {
            throw new IllegalArgumentError("Invalid minVersion value");
        }

        // emit new modelMinVersion value only if superior to the existing one
        if (minVersion > this.modelMinVersion) {
            this._modelMinVersionSub$.next(minVersion);
        }
    }

    /**
     * Apply the state of another orm into this one.
     *
     * Attempt to minimize the changes on the current orm as much as possible
     *
     * @param entityContext the other orm context
     */
    rebaseFrom(entityContext: EntityContext) {
        // TODO : remove hack
        // HACK : force the `modelMinVersion` to trigger `integrityCheck`
        this._modelMinVersionSub$.next(entityContext.modelMinVersion);
        // this.ensureModelMinVersion(entityContext.modelMinVersion);

        // create missing entities
        for (const entityType of this.entityTypes.byIndex) {
            const entityName = entityType.entityName;
            const myEntities = this._entitiesByType[entityName];
            const hisEntities = entityContext._entitiesByType[entityName];

            for (const entityId in hisEntities) {
                if (entityId in myEntities) {
                    continue;
                }
                this.createEntity(entityName, entityId);
            }
        }

        // copy changes
        for (const entityType of this.entityTypes.byIndex) {
            const entityName = entityType.entityName;
            const metadata = entityType.metadata;
            const myEntities = this._entitiesByType[entityName];
            const hisEntities = entityContext._entitiesByType[entityName];

            for (const entityId in hisEntities) {
                const myEntity = myEntities[entityId];
                const hisEntity = hisEntities[entityId];

                // sync properties
                for (const name in metadata) {
                    const property = metadata[name];

                    if (property.calculated) {
                        continue;
                    }

                    if (property.relation === true) {
                        if (!property.single) {
                            continue; // only single relationships
                        }

                        // update relation, find the corresponding value in my entities
                        const value = hisEntity[name];
                        if (!value) {
                            myEntity[name] = null;
                        } else {
                            const target = this.getEntity(property.target.entityName, value.id);
                            if (!target) {
                                throw new ORMError("missing entity reference", entityType.entityName, entityId);
                            }
                            myEntity[name] = target;
                        }
                    } else {
                        if (property.primaryKey) {
                            continue;
                        }

                        if (property.export === ExportKind.cache) {
                            continue;
                        }

                        // update value
                        myEntity[name] = hisEntity[name];
                    }
                }

                // sync attachments
                const myAttachments = myEntity.getAttachments();
                const hisAttachments = hisEntity.getAttachments();
                for (const key in hisAttachments) {
                    const hisAttachment = hisAttachments[key];
                    if (!(key in myAttachments) || myAttachments[key] !== hisAttachment) {
                        myEntity.setAttachmentForKey(key, hisAttachment);
                    }
                }
                for (const key in myAttachments) {
                    if (!(key in hisAttachments)) {
                        myEntity.setAttachmentForKey(key, null);
                    }
                }
            }
        }

        // remove entities that are no longer there
        for (const entityType of this.entityTypes.byIndex) {
            const entityName = entityType.entityName;
            const myEntities = this._entitiesByType[entityName];
            const hisEntities = entityContext._entitiesByType[entityName];

            for (const entityId in myEntities) {
                if (entityId in hisEntities) {
                    continue;
                }
                this.deleteEntity(myEntities[entityId]);
            }
        }

        // at this point, both entity context have the same state but we still need to synchronize the updates
        this._createdEntities = {};
        for (const entityName in entityContext._createdEntities) {
            const myCreations = (this._createdEntities[entityName] = {});
            const hisCreations = entityContext._createdEntities[entityName];
            for (const entityId in hisCreations) {
                myCreations[entityId] = this.getEntity(entityName, entityId);
            }
        }
        this._modifiedEntities = {};
        for (const entityName in entityContext._modifiedEntities) {
            const myUpdates = (this._modifiedEntities[entityName] = {});
            const hisUpdates = entityContext._modifiedEntities[entityName];
            for (const entityId in hisUpdates) {
                myUpdates[entityId] = Object.assign(hisUpdates[entityId]);
            }
        }
        this._deletedEntities = {};
        for (const entityName in entityContext._deletedEntities) {
            const mySuppressions = (this._deletedEntities[entityName] = {});
            const hisSuppressions = entityContext._deletedEntities[entityName];
            for (const entityId in hisSuppressions) {
                mySuppressions[entityId] = hisSuppressions[entityId];
            }
        }
        this._createdAttachments = {};
        for (const entityName in entityContext._createdAttachments) {
            const myUpdates = (this._createdAttachments[entityName] = {});
            const hisUpdates = entityContext._createdAttachments[entityName];
            for (const entityId in hisUpdates) {
                myUpdates[entityId] = Object.assign(hisUpdates[entityId]);
            }
        }
        this._updatedAttachments = {};
        for (const entityName in entityContext._updatedAttachments) {
            const myUpdates = (this._updatedAttachments[entityName] = {});
            const hisUpdates = entityContext._updatedAttachments[entityName];
            for (const entityId in hisUpdates) {
                myUpdates[entityId] = Object.assign(hisUpdates[entityId]);
            }
        }
    }

    /**
     * Clone the entity context.
     * Keep the same schema model (`entityTypes, dynamicEntities, validators, modelVersion, modelMinVersion`).
     */
    clone(): EntityContext {
        const clonedEntityContext = new EntityContext(
            this.localizationService,
            this.internalizationService,
            this.entityTypes,
            this.dynamicEntities,
            this.validators,
            {
                versionIdentifiers: [this._modelVersion],
                minVersion: this._modelMinVersionSub$.getValue(),
                patchHandlers: this.patchHandlers,
            },
        );
        // Copy actual project entity context into the new one
        clonedEntityContext.rebaseFrom(this);
        return clonedEntityContext;
    }

    /**
     * Get an entity by entity type and entity id
     *
     * @param entityName entity type name
     * @param entityId entity id
     * @return the entity or null
     */
    getEntity<K extends keyof EntityTypeMap>(entityName: K, entityId: string): EntityTypeMap[K] | null;
    getEntity<T extends BaseEntity>(entityName: string, entityId: string): T | null;
    getEntity(entityName: string, entityId: string): BaseEntity | null {
        const entity = this._entitiesByType[entityName][entityId];
        if (!entity) {
            return null;
        }
        return entity;
    }
    /**
     * Check if an entity once existed then was deleted
     *
     * @param entityName entity type name
     * @param entityId entity id
     * @return true if the entity once existed then was deleted
     */
    isEntityDeleted(entityName: string, entityId: string) {
        return this._deletedEntitiesByType[entityName][entityId];
    }

    createEntity<K extends keyof EntityTypeMap>(entityName: K, entityId?: string): EntityTypeMap[K];
    createEntity<T extends BaseEntity>(entityName: string, entityId?: string): T;
    createEntity(entityName: string, entityId?: string): BaseEntity {
        const dynamicEntity: DynamicEntityConstructor = this.dynamicEntities.dynamicEntityByName[entityName];
        if (!dynamicEntity) {
            throw new ORMError("No such entity name", entityName);
        }

        if (!entityId) {
            entityId = generateId();
        }

        if (entityId in this._entitiesByType[entityName]) {
            throw new ORMError("A entity already exists", entityName, entityId);
        }

        const entity = new dynamicEntity(this, entityId);

        if (entity.id in this.deletedEntities[entityName]) {
            // entity recreation (may happen with deterministic id generation) -> mark the entity as modified
            delete this._deletedEntities[entityName][entity.id];
            delete this._deletedEntitiesByType[entityName][entity.id];
            this._modifiedEntities[entityName][entity.id] = {};
        } else {
            this._createdEntities[entityName][entityId] = entity;
        }

        this.setDirty(true);
        this._entitiesByType[entityName][entityId] = entity;

        toBaseEntityInternal(entity).entityContext = this;
        this._notifyListeners("entityCreated", entity);

        return entity;
    }
    deleteEntity(entity: BaseEntity) {
        if (entity.entityContext !== null && entity.entityContext !== this) {
            const error = new ORMError("Illegal attempt to delete an entity of another EntityContext");
            error.addContext("Archipad", {
                "entity to delete": {
                    entityName: entity.entityType.entityName,
                    entityId: entity.id,
                },
            });
            throw error;
        }
        if (entity.isDeleted()) {
            return; // already deleted
        }
        if (toBaseEntityInternal(entity).state.updating) {
            return; // already updating
        }

        // prevent update loops
        toBaseEntityInternal(entity).state.updating = true;

        const entityType = entity.entityType;
        const entityName = entityType.entityName;

        // notify listeners that the entity is about to be deleted
        this._notifyListeners("willDeleteEntity", entity);

        // handle delete cascade and clear entity relationships
        for (const propertyName in entityType.metadata) {
            const property = entityType.metadata[propertyName];
            if (!property.relation || property.calculated) {
                continue;
            }

            const relation = property;
            if (relation.single) {
                if (entity[propertyName]) {
                    const target = entity[propertyName];
                    // clear relation
                    entity[propertyName] = null;
                    this._updateRelationInverse(entity, property, null, target);
                    // have to notify listeners manually here because this entity is already updating
                    this._notifyListeners("entityUpdated", entity, propertyName, target, null);
                    // cascade delete
                    if (relation.deleteRule === "cascade") {
                        this.deleteEntity(target);
                    }
                }
            } else {
                const targets = entity[propertyName];
                if (targets.length) {
                    // clear relation
                    for (const target of targets) {
                        this._updateRelationInverse(entity, property, null, target);
                    }
                    // cascade delete
                    if (relation.deleteRule == "cascade") {
                        for (const target of targets) {
                            this.deleteEntity(target);
                        }
                    }
                    targets.splice(0, targets.length);
                }
            }
        }

        // delete attachments
        const attachments = entity.getAttachments();
        for (const key in attachments) {
            this.entityAttachmentUpdated(entity, key, attachments[key], null);
        }

        if (entity.id in this.createdEntities[entityName]) {
            // delete an entity that was created in the current patch -> no longer created
            delete this._createdEntities[entityName][entity.id];
        } else if (entity.id in this.modifiedEntities[entityName]) {
            // delete an entity that was modified in the current patch -> no longer modified and mark as deleted
            delete this._modifiedEntities[entityName][entity.id];
            this._deletedEntities[entityName][entity.id] = true;
        } else {
            // otherwise mark as deleted
            this._deletedEntities[entityName][entity.id] = true;
        }
        this._deletedEntitiesByType[entityName][entity.id] = true;

        delete this._entitiesByType[entityName][entity.id];
        toBaseEntityInternal(entity).entityContext = null;

        toBaseEntityInternal(entity).state.updating = false;

        // entity deleted
        this.setDirty(true);
        this._notifyListeners("entityDeleted", entity);
    }

    setFlushDataHandler(handler: FlushDataHandler) {
        this._flushDataHandler = handler;
    }
    flushData(): Observable<void> {
        if (!this._flushDataHandler) {
            return throwError(new IllegalStateError("No flush data handler"));
        }
        return this._flushDataHandler();
    }

    async executeWithoutValidators(callback: () => Promise<void> | void): Promise<void> {
        this._validatorsEnabled = false;
        try {
            await callback();
        } finally {
            this._validatorsEnabled = true;
        }
    }

    private _notifyListeners(...args: any[]) {
        const notification = args as Notification;

        if (this._validatorsEnabled) {
            for (const validator of this.validators) {
                validator.validateNotification(notification);
            }
        }

        this._notifications.next(notification);
    }

    entityUpdated(entity: BaseEntity, name: string, oldVal: any, val: any) {
        if (toBaseEntityInternal(entity).state.updating) {
            return; // already updating
        }
        toBaseEntityInternal(entity).state.updating = true;

        const entityType = entity.entityType;
        const entityName = entityType.entityName;
        const property = entityType.metadata[name];

        if (property.relation) {
            if (!property.single) {
                throw new ORMError('Assignment of multiple relationship "' + name + '" is not supported');
            }
        }

        if (entity.id in this.createdEntities[entityName]) {
            // keep the changes on created entities
            this.createdEntities[entityName][entity.id][name] = val;
        } else {
            // mark entity changed and keep the initial value for modified property
            let modifiedEntity = this.modifiedEntities[entityName][entity.id];
            if (!modifiedEntity) {
                this._modifiedEntities[entityName][entity.id] = modifiedEntity = {};
            }
            if (modifiedEntity[name] === undefined) {
                modifiedEntity[name] = oldVal;
            }
        }

        // maintain relationship information
        if (property.relation) {
            this._updateRelationInverse(entity, property, val, oldVal);
        }

        toBaseEntityInternal(entity).state.updating = false;

        this.setDirty(true);
        this._notifyListeners("entityUpdated", entity, name, oldVal, val);

        // cleared an owning property
        if (oldVal && !val && property.relation && property.owning) {
            let isOrphan = true;
            for (const name in entityType.metadata) {
                const property = entityType.metadata[name];
                if (property.relation && property.owning && this[name]) {
                    isOrphan = false; // entity still has a non null owning property
                    break;
                }
            }

            // the entity is now orphan, delete it
            if (isOrphan) {
                this.deleteEntity(entity);
            }
        }
    }
    entityAttachmentUpdated(entity: BaseEntity, key: string, oldAttachment: any, attachment: any) {
        const entityName = entity.entityType.entityName;

        if (!oldAttachment && attachment) {
            // adding a new attachment
            if (
                this.updatedAttachments?.[entityName]?.[entity.id] &&
                key in this.updatedAttachments[entityName][entity.id]
            ) {
                this._updatedAttachments[entityName][entity.id][key] = attachment;
            } else {
                if (!this.createdAttachments[entityName]) {
                    this._createdAttachments[entityName] = {};
                }

                if (!this.createdAttachments[entityName][entity.id]) {
                    this._createdAttachments[entityName][entity.id] = {};
                }

                this._createdAttachments[entityName][entity.id][key] = attachment;
            }
        } else if (!attachment) {
            // removing an attachment
            if (
                this.createdAttachments[entityName] &&
                this.createdAttachments[entityName][entity.id] &&
                this.createdAttachments[entityName][entity.id][key]
            ) {
                delete this._createdAttachments[entityName][entity.id][key];
            } else {
                if (!this.updatedAttachments[entityName]) {
                    this._updatedAttachments[entityName] = {};
                }

                if (!this.updatedAttachments[entityName][entity.id]) {
                    this._updatedAttachments[entityName][entity.id] = {};
                }

                this._updatedAttachments[entityName][entity.id][key] = attachment;
            }
        } else {
            // updating an attachment
            if (
                this.createdAttachments[entityName] &&
                this.createdAttachments[entityName][entity.id] &&
                this.createdAttachments[entityName][entity.id][key]
            ) {
                this._createdAttachments[entityName][entity.id][key] = attachment;
            } else {
                if (!this.updatedAttachments[entityName]) {
                    this._updatedAttachments[entityName] = {};
                }

                if (!this.updatedAttachments[entityName][entity.id]) {
                    this._updatedAttachments[entityName][entity.id] = {};
                }

                this._updatedAttachments[entityName][entity.id][key] = attachment;
            }
        }

        // also mark the associated entity as modified if this is not already the case
        if (!this.createdEntities[entityName][entity.id] && !this.modifiedEntities[entityName][entity.id]) {
            this._modifiedEntities[entityName][entity.id] = {};
        }

        this.setDirty(true);

        this._notifyListeners("attachmentUpdated", entity, key, oldAttachment, attachment);
    }
}
